import type { LotLiveState } from '@skinstep/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { RepeatBadge } from '../components/BidderPill';
import { BidHistoryRow } from '../components/BidHistoryRow';
import { FullBidHistory } from '../components/FullBidHistory';
import { LotHero } from '../components/LotHero';
import { LotMainButton } from '../components/LotMainButton';
import { PanelLabel } from '../components/PanelLabel';
import { ProvablyFairBadge } from '../components/ProvablyFairBadge';
import type { Bidder, BidEntry, Lot } from '../data/mockLots';
import { buyNowQuoteQueryKey, useBuyNowQuote } from '../hooks/useBuyNowQuote';
import { useLotDetail } from '../hooks/useLotDetail';
import { deriveLotState, useLotState } from '../hooks/useLotState';
import { useLotWs } from '../hooks/useLotWs';
import { useSession } from '../hooks/useSession';
import { ApiError, api } from '../lib/apiClient';
import { formatAgo } from '../lib/format';
import { applyLiveState, lotFromDetail, placeholderBidder } from '../lib/lots';
import { revealProps } from '../lib/motion';
import { adjustBidBalance } from '../lib/session';
import { openTgLink, shareLot } from '../lib/tg';

type Props = {
  lotId: string;
  initialLot?: Lot;
  onBack: () => void;
};

const VISIBLE_BIDS = 10;
const HISTORY_LIMIT = 60;

// Entrance cascade for the lot page's top-level blocks (hero → chat → history →
// buy now → provably fair). Slightly larger step than the home feed since the
// blocks are taller.
const LOT_CASCADE = { base: 60, step: 70 };

type BidErrorCode =
  | 'INSUFFICIENT_BALANCE'
  | 'EXPIRED'
  | 'NOT_LIVE'
  | 'NOT_INITIALIZED'
  | 'FREE_BID_TAKEN'
  | 'FREE_BID_INELIGIBLE';

const BID_ERROR_COPY: Record<BidErrorCode, string> = {
  INSUFFICIENT_BALANCE: 'Not enough bids on your balance.',
  EXPIRED: 'Timer expired — this round is over.',
  NOT_LIVE: 'Lot is not live right now.',
  NOT_INITIALIZED: 'Lot is not ready yet — try again in a moment.',
  FREE_BID_TAKEN: 'Too slow — the free bid was already taken.',
  FREE_BID_INELIGIBLE: 'No free bid available on this lot for you.',
};

function isBidErrorCode(code: string | undefined): code is BidErrorCode {
  return (
    code === 'INSUFFICIENT_BALANCE' ||
    code === 'EXPIRED' ||
    code === 'NOT_LIVE' ||
    code === 'NOT_INITIALIZED' ||
    code === 'FREE_BID_TAKEN' ||
    code === 'FREE_BID_INELIGIBLE'
  );
}

function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function LastBidSlot({
  bidder,
  label,
  tone = 'primary',
  repeatCount,
  meta,
  placeholderHint,
}: {
  bidder?: Bidder;
  label: string;
  tone?: 'primary' | 'success' | 'muted';
  repeatCount?: number;
  meta?: ReactNode;
  placeholderHint?: string;
}) {
  const labelToneClass =
    tone === 'success' ? 'text-success' : tone === 'muted' ? 'text-fg-dim' : 'text-primary';

  const avatar = bidder ? (
    <div
      className="w-7 h-7 rounded-full font-mono font-semibold text-bg flex items-center justify-center shrink-0"
      style={{
        background: `linear-gradient(135deg, hsl(${bidder.hue}, 90%, 65%) 0%, hsl(${(bidder.hue + 40) % 360}, 85%, 50%) 100%)`,
        boxShadow: `0 0 0 1px hsl(${bidder.hue}, 60%, 30%), 0 0 10px hsla(${bidder.hue}, 80%, 50%, 0.3)`,
        fontSize: 12,
      }}
    >
      {bidder.initial}
    </div>
  ) : (
    <div className="w-7 h-7 rounded-full border border-dashed border-fg-dim/50 bg-bg/40 font-mono text-fg-dim flex items-center justify-center shrink-0 text-[13px]">
      —
    </div>
  );

  const body = (
    <div className="flex flex-col items-end gap-1.5 min-w-0 max-w-[200px]">
      <span className={`font-mono text-[9px] tracking-[0.22em] ${labelToneClass}`}>{label}</span>
      <div className="flex items-center gap-2 min-w-0 max-w-full">
        {repeatCount != null && <RepeatBadge count={repeatCount} />}
        {bidder ? (
          <span className="font-medium text-[15px] truncate text-fg">{bidder.firstName}</span>
        ) : (
          <span className="font-mono text-[9px] tracking-[0.22em] text-fg-dim">
            {placeholderHint ?? 'NO BIDDER'}
          </span>
        )}
        {avatar}
      </div>
      {bidder?.username ? (
        <span className="font-mono text-[10px] text-fg-dim leading-none truncate max-w-full">
          @{bidder.username}
        </span>
      ) : null}
      {meta && (
        <span className="font-mono text-[10px] text-fg-muted tabular-nums leading-none">
          {meta}
        </span>
      )}
    </div>
  );

  if (bidder?.username) {
    return (
      <a
        href={`https://t.me/${bidder.username}`}
        target="_blank"
        rel="noreferrer"
        className="hover:opacity-90 transition-opacity"
      >
        {body}
      </a>
    );
  }

  return body;
}

export function LotDetail({ lotId, initialLot, onBack }: Props) {
  const session = useSession();
  const queryClient = useQueryClient();
  const detailQuery = useLotDetail(lotId);

  const baseLot = useMemo<Lot | null>(
    () => (detailQuery.data ? lotFromDetail(detailQuery.data) : (initialLot ?? null)),
    [detailQuery.data, initialLot],
  );

  const isLiveLot = baseLot != null && (baseLot.startsAt == null || baseLot.startsAt <= Date.now());
  const isClosedLot = baseLot != null && deriveLotState(baseLot) === 'closed';

  const {
    liveState,
    status: wsStatus,
    pushLiveState,
  } = useLotWs(lotId, {
    enabled: baseLot != null && !isClosedLot && isLiveLot,
    onBidPlaced: (s) => appendBidFromWs(s),
    onLotClosed: () => {
      queryClient.invalidateQueries({ queryKey: ['lot', lotId] });
      // Winner is decided in LotDO.alarm — refresh the quote so the BIN CTA
      // hides for the winner instead of staying stuck on the pre-close state.
      queryClient.invalidateQueries({ queryKey: buyNowQuoteQueryKey(lotId) });
    },
  });

  const effectiveLot = useMemo<Lot | null>(
    () => (baseLot ? applyLiveState(baseLot, liveState) : null),
    [baseLot, liveState],
  );

  const state = useLotState(effectiveLot);

  const chatDeepLink = detailQuery.data?.chat?.deep_link ?? null;

  const [history, setHistory] = useState<BidEntry[]>([]);
  const [flash, setFlash] = useState(0);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [bidError, setBidError] = useState<string | null>(null);
  // Latched once this client learns the free slot is gone (lost the race, or not
  // eligible). Flips the button to the paid bid immediately, without waiting for
  // the bids_count bump to arrive over WS.
  const [freeSlotTaken, setFreeSlotTaken] = useState(false);

  function appendBidFromWs(s: LotLiveState) {
    setFlash((f) => f + 1);
    setHistory((h) => {
      if (s.bids_count > 0 && h.some((e) => e.bidIndex === s.bids_count)) return h;
      return [
        {
          ts: Date.now(),
          bidder: placeholderBidder(
            s.last_bidder_user_id,
            s.last_bidder_first_name,
            s.last_bidder_username,
          ),
          price: s.current_price_cents / 100,
          bidIndex: s.bids_count > 0 ? s.bids_count : undefined,
          extensionMs: s.last_timer_extension_ms ?? undefined,
          userBidSeq: s.last_bidder_user_bid_seq ?? undefined,
        } satisfies BidEntry,
        ...h,
      ].slice(0, HISTORY_LIMIT);
    });
  }

  useEffect(() => {
    const recent = detailQuery.data?.recent_bids;
    if (!recent || recent.length === 0) return;
    setHistory((h) => {
      const seen = new Map(
        h
          .filter((e): e is BidEntry & { bidIndex: number } => e.bidIndex != null)
          .map((e) => [e.bidIndex, e]),
      );
      let changed = false;
      const merged: BidEntry[] = recent.map((rb) => {
        const existing = seen.get(rb.bid_index);
        const next: BidEntry = {
          ts: rb.placed_at,
          bidder: placeholderBidder(rb.user_id, rb.first_name, rb.username),
          price: rb.price_after_cents / 100,
          bidIndex: rb.bid_index,
          extensionMs: rb.timer_extension_ms ?? undefined,
          userBidSeq: rb.user_bid_seq ?? undefined,
        };
        if (!existing || existing.extensionMs !== next.extensionMs) changed = true;
        seen.delete(rb.bid_index);
        return next;
      });
      // Keep WS-only rows that the API hasn't returned yet (oldest, beyond limit).
      for (const stale of seen.values()) merged.push(stale);
      if (!changed && merged.length === h.length) return h;
      return merged.sort((a, b) => (b.bidIndex ?? 0) - (a.bidIndex ?? 0)).slice(0, HISTORY_LIMIT);
    });
  }, [detailQuery.data]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const bidMutation = useMutation({
    mutationFn: async (freeIntent: boolean) => {
      return await api.post<{ ok: true; state: LotLiveState }>(`/api/lots/${lotId}/bid`, {
        idempotency_key: makeIdempotencyKey(),
        free_intent: freeIntent,
      });
    },
    onMutate: (freeIntent: boolean) => {
      // Decrement now so the on-button counter ticks down on tap, not after the
      // server roundtrip (200-500ms on TG-WebView over VPN). Roll back in
      // onError if the bid is rejected (EXPIRED / INSUFFICIENT_BALANCE / etc).
      // A free bid debits nothing — skip the optimistic decrement entirely.
      setBidError(null);
      if (freeIntent) return { decremented: false as boolean };
      adjustBidBalance(-1);
      return { decremented: true as boolean };
    },
    onError: (err, _freeIntent, ctx) => {
      if (ctx?.decremented) adjustBidBalance(+1);
      if (err instanceof ApiError) {
        // Free slot is gone (lost the race, or not eligible): flip this client's
        // button to the paid bid now rather than waiting for the WS echo.
        if (err.code === 'FREE_BID_TAKEN' || err.code === 'FREE_BID_INELIGIBLE') {
          setFreeSlotTaken(true);
        }
        const copy = isBidErrorCode(err.code) ? BID_ERROR_COPY[err.code] : err.message;
        setBidError(copy);
      } else {
        setBidError('Bid failed — check your connection and retry.');
      }
    },
    onSuccess: (res) => {
      // Apply state from the HTTP response immediately — don't wait for the
      // WS broadcast echo. TG WebView delivers inbound WS frames with
      // 1-2s scheduling delay on idle sockets (see prior observability run),
      // while the HTTP response is on a hot-priority TLS connection and
      // returns in ~200ms. The WS broadcast for the same bid_index arrives
      // later and dedupes inside appendBidFromWs.
      pushLiveState(res.state);
      appendBidFromWs(res.state);
      // Viewer's spent count changed — refresh the BIN quote. Chat bidders
      // don't trigger this (their bids don't move the viewer's discount).
      queryClient.invalidateQueries({ queryKey: buyNowQuoteQueryKey(lotId) });
    },
  });

  const uniqueBidders = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of history) ids.add(entry.bidder.id);
    return ids.size;
  }, [history]);

  const visible = useMemo(() => history.slice(0, VISIBLE_BIDS), [history]);

  const maxBidIndex = useMemo(
    () => visible.reduce((m, e) => Math.max(m, e.bidIndex ?? 0), 0),
    [visible],
  );
  const indexWidth = Math.max(2, String(maxBidIndex).length);

  const lastBidderCount = history[0]?.userBidSeq ?? 0;

  const balanceBids = session?.user.bid_balance ?? 0;

  // Buy Now eligibility + price. The quote is server-authoritative: we
  // can't derive bids_spent from a paginated history (would lie on closed lots
  // with >RECENT_BIDS_LIMIT bids, recreating the DealDash 2017 setup).
  const buyNowQuoteQuery = useBuyNowQuote(lotId, {
    enabled: state != null && state !== 'scheduled',
  });
  const buyNowQuote = buyNowQuoteQuery.data ?? null;
  const viewerBidsCount = buyNowQuote?.viewer_bids_count ?? 0;
  const buyNowVisible = buyNowQuote?.available ?? false;
  const buyNowPrice = buyNowQuote ? buyNowQuote.buy_now_price_cents / 100 : 0;
  const viewerId = session?.user.id ?? null;
  const viewerIsWinner = Boolean(
    effectiveLot && viewerId != null && effectiveLot.winner?.id === `u${viewerId}`,
  );

  if (!effectiveLot || !state) {
    const notFound =
      detailQuery.isError &&
      detailQuery.error instanceof ApiError &&
      detailQuery.error.status === 404;
    return <LotDetailFallback onBack={onBack} notFound={notFound} />;
  }

  return (
    <div className="min-h-dvh mx-auto max-w-[480px] relative">
      <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-edge">
        <div className="px-3 h-12 grid grid-cols-[40px_1fr_40px] items-center">
          <button
            type="button"
            onClick={onBack}
            className="w-10 h-10 -ml-2 flex items-center justify-center text-fg hover:text-primary transition-colors"
            aria-label="Back to lots"
          >
            <span className="font-mono text-[18px] leading-none">←</span>
          </button>
          <div className="flex justify-center">
            <span className="font-mono text-[11px] tracking-[0.3em] text-fg-muted">
              LOT / <span className="text-fg">{effectiveLot.code}</span>
              {wsStatus === 'connecting' && <span className="text-warning ml-2">·</span>}
              {wsStatus === 'open' && <span className="text-success ml-2">·</span>}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              const title = effectiveLot.skinName || 'this skin';
              shareLot(lotId, `Check out ${title} on SkinStep`);
            }}
            className="w-10 h-10 -mr-2 flex items-center justify-center text-fg-muted hover:text-fg transition-colors"
            aria-label="Share lot"
          >
            <span className="font-mono text-[14px] leading-none">⇪</span>
          </button>
        </div>
      </header>

      <main className={mainBottomPadding({ state, balanceBids, buyNowVisible, viewerIsWinner })}>
        <div {...revealProps(0, { ...LOT_CASCADE, className: 'px-3 pt-3' })}>
          <LotHero
            lot={effectiveLot}
            state={state}
            livePrice={state === 'live' ? effectiveLot.currentPrice : undefined}
            flashTrigger={flash}
            timerEndsAt={state === 'live' ? effectiveLot.timerEndsAt : undefined}
            rightSlot={
              state === 'live' ? (
                history[0] ? (
                  <LastBidSlot
                    bidder={history[0].bidder}
                    label="LAST BID"
                    tone="primary"
                    repeatCount={lastBidderCount}
                  />
                ) : (
                  <LastBidSlot label="LAST BID" tone="primary" placeholderHint="NO BIDDER" />
                )
              ) : state === 'closed' ? (
                effectiveLot.winner ? (
                  <LastBidSlot
                    bidder={effectiveLot.winner}
                    label="WON BY"
                    tone="success"
                    repeatCount={lastBidderCount}
                  />
                ) : (
                  <LastBidSlot
                    label="NO WINNER"
                    tone="muted"
                    placeholderHint="Closed unsold"
                    meta={
                      effectiveLot.closedAt ? formatAgo(effectiveLot.closedAt, nowTick) : undefined
                    }
                  />
                )
              ) : null
            }
          />
        </div>

        {chatDeepLink && (
          <button
            type="button"
            onClick={() => openTgLink(chatDeepLink)}
            {...revealProps(1, {
              ...LOT_CASCADE,
              className:
                'mx-3 mt-3 w-[calc(100%-1.5rem)] h-11 border border-primary/40 bg-primary/5 text-primary flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-primary/10 hover:border-primary/70 active:scale-[0.99] transition-all',
            })}
          >
            <span aria-hidden>◐</span>
            <span>Lot chat</span>
            <span>↗</span>
          </button>
        )}

        {state === 'scheduled' && (
          <div
            {...revealProps(1, {
              ...LOT_CASCADE,
              className:
                'mx-3 mt-4 border border-dashed border-edge px-4 py-6 text-center bg-bg-elev/30',
            })}
          >
            <p className="font-mono text-[10px] tracking-[0.25em] text-warning">
              FIRST BID LOCKS YOUR SLOT
            </p>
            <p className="text-[13px] text-fg-muted mt-2 leading-relaxed">
              Auction opens soon. Be the first to drop a bid when the timer hits zero.
            </p>
          </div>
        )}

        {state !== 'scheduled' && (visible.length > 0 || state === 'closed') && (
          <div {...revealProps(2, { ...LOT_CASCADE, className: 'mt-5' })}>
            <PanelLabel
              label="BID HISTORY"
              right={`${uniqueBidders} bidders`}
              className="px-3 mb-2"
            />
            {state === 'closed' ? (
              <FullBidHistory lotId={lotId} />
            ) : (
              <>
                {/* Reserve 10-row slot (36px each) so prepending new WS bids
                    doesn't reflow content below the section. */}
                <div className="flex flex-col min-h-[360px]">
                  {visible.map((entry, idx) => (
                    <BidHistoryRow
                      key={`${entry.ts}-${entry.bidder.id}-${entry.price}`}
                      entry={entry}
                      isLatest={idx === 0}
                      count={entry.userBidSeq}
                      indexWidth={indexWidth}
                    />
                  ))}
                </div>
                <p className="mt-2 py-3 px-3 text-center font-mono text-[10px] tracking-[0.22em] text-fg-dim border-t border-edge">
                  full bid history will be available after the lot closes
                </p>
              </>
            )}
          </div>
        )}

        {bidError && (
          <div
            {...revealProps(0, {
              base: 0,
              className: 'mx-3 mt-4 border border-danger/40 bg-danger/5 px-3 py-2 text-center',
            })}
          >
            <p className="font-mono text-[10px] tracking-[0.22em] text-danger">{bidError}</p>
          </div>
        )}

        {/* Inline Buy Now lives only when the floating BID is the active CTA —
            i.e. user can still bid. When user runs out or the lot closes, the
            floating footer takes it over so we'd be duplicating; skip here. */}
        {state === 'live' && buyNowVisible && balanceBids > 0 && (
          <div
            {...revealProps(3, {
              ...LOT_CASCADE,
              className:
                'mx-3 mt-5 border border-edge bg-bg-elev/40 px-4 py-3 flex items-center justify-between gap-3',
            })}
          >
            <div className="min-w-0">
              <p className="font-mono text-[9px] tracking-[0.25em] text-fg-muted">BUY NOW</p>
              <p className="text-[12px] text-fg mt-1 truncate">
                Skip the auction — claim it at market.
              </p>
            </div>
            <button
              type="button"
              className="font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10 transition-colors shrink-0"
            >
              ${buyNowPrice.toFixed(2)}
            </button>
          </div>
        )}

        <ProvablyFairBadge
          provablyFair={detailQuery.data?.provably_fair ?? null}
          {...revealProps(4, LOT_CASCADE)}
        />
      </main>

      <LotMainButton
        state={state}
        balanceBids={balanceBids}
        timerEndsAt={state === 'live' ? effectiveLot.timerEndsAt : undefined}
        bidsCount={effectiveLot.bidsCount}
        freeSlotTaken={freeSlotTaken}
        onBid={(freeIntent) => bidMutation.mutate(freeIntent)}
        buyNowVisible={buyNowVisible}
        buyNowPrice={buyNowPrice}
        viewerBidsCount={viewerBidsCount}
        marketPrice={effectiveLot.marketPrice}
        viewerIsWinner={viewerIsWinner}
        onTopUp={() => {
          // Top up screen is a future S4 work item (see auto-memory
          // [[bid-pack-purchase-disclosure]]). Visible CTA already
          // delivers the right message; the action becomes live then.
          console.info('lot.top_up_clicked', { lotId });
        }}
        onClaim={() => {
          // Trade URL flow lives in S3 alongside payout-worker. For now this
          // is a placeholder so winners see the right CTA instead of nothing.
          console.info('lot.claim_clicked', { lotId });
        }}
      />
    </div>
  );
}

// Match the floating CTA stack height so the last block on the page (Provably
// Fair, bid history footer) keeps ~24px of breathing room above the bar.
function mainBottomPadding({
  state,
  balanceBids,
  buyNowVisible,
  viewerIsWinner,
}: {
  state: ReturnType<typeof useLotState>;
  balanceBids: number;
  buyNowVisible: boolean;
  viewerIsWinner: boolean;
}): string {
  if (state === 'closed') {
    if (viewerIsWinner) return 'pb-32'; // Claim CTA (h-14) + caption
    return buyNowVisible ? 'pb-32' : 'pb-6';
  }
  const outOfBids = state === 'live' && balanceBids <= 0;
  if (outOfBids && buyNowVisible) return 'pb-44'; // BID + Top up stack
  if (outOfBids) return 'pb-32'; // Top up solo + caption
  return 'pb-28'; // single CTA + caption
}

function LotDetailFallback({ onBack, notFound }: { onBack: () => void; notFound: boolean }) {
  return (
    <div className="min-h-dvh mx-auto max-w-[480px] relative">
      <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-edge">
        <div className="px-3 h-12 grid grid-cols-[40px_1fr_40px] items-center">
          <button
            type="button"
            onClick={onBack}
            className="w-10 h-10 -ml-2 flex items-center justify-center text-fg hover:text-primary transition-colors"
            aria-label="Back to lots"
          >
            <span className="font-mono text-[18px] leading-none">←</span>
          </button>
          <span className="font-mono text-[11px] tracking-[0.3em] text-fg-muted text-center">
            LOT
          </span>
          <span />
        </div>
      </header>
      <main className="px-4 pt-16 text-center">
        {notFound ? (
          <>
            <p className="font-mono text-[10px] tracking-[0.25em] text-danger">LOT NOT FOUND</p>
            <p className="text-[13px] text-fg-muted mt-2">
              This lot doesn’t exist or has been removed.
            </p>
          </>
        ) : (
          <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">LOADING LOT…</p>
        )}
      </main>
    </div>
  );
}
